import type { FC } from "react";
import { Settings, LogOut, User } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "src/components/ui/dropdown-menu";

interface UserMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const UserMenu: FC<UserMenuProps> = ({ isOpen, onToggle, onClose }) => {
  const { t } = useTranslation(["common", "translation"]);
  const { user, logout } = useAuth();

  const handleSignOut = async () => {
    onClose();
    await logout();
  };

  const displayName = user?.username || user?.email;
  const initial = (user?.username || "U").charAt(0).toUpperCase();

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={open => {
        if (!open) onClose();
        else onToggle();
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={e => {
            e.preventDefault();
            onToggle();
          }}
          className="flex items-center gap-2 p-1.5 rounded-md hover:bg-gray-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        >
          <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
            {user?.username ? (
              <span className="text-sm font-bold text-white">{initial}</span>
            ) : (
              <User className="h-5 w-5 text-white" />
            )}
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[240px]">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {displayName || t("user", { ns: "translation" })}
            </p>
            <p className="text-xs text-gray-500 break-words">
              {user?.email || user?.username}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to="/account/settings"
            className="flex items-center gap-3 cursor-pointer"
            onClick={onClose}
          >
            <Settings className="h-4 w-4 text-gray-400" />
            <span>{t("settings")}</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleSignOut}
          className="flex items-center gap-3 cursor-pointer"
        >
          <LogOut className="h-4 w-4 text-gray-400" />
          <span>{t("signOut")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
